import { Journey } from '../../Types/journey';
import { useTranslation } from 'react-i18next';
import './CoursesJourneyList.scss';
import { getPathWithRootPath } from '../../Services/common';
import { useEffect, useMemo, useState } from 'react';
import { BizSelect } from '../Mini/BizSelect';
import BizTabs from '../Mini/BizTabs';

const ALL_COLLECTIONS_ID = 'all';
const DEFAULT_EO_MINUTES = 5;

export type JourneyTypeLabel = {
	type: string;
	overrideLabels?: string[];
	overrideIcon?: string;
};

export type CoursesJourney = Journey & {
	EngageObjects?: Array<{ Status?: number; EstimatedTimeInMinutes?: number; ImageUrl?: string } & Record<string, unknown>>;
	OverrideTypeLabels?: JourneyTypeLabel[];
};

type CoursesJourneyListProps = {
	items: CoursesJourney[];
	onJourneyClick: (journey: CoursesJourney) => void;
	limit?: number;
	hideCollectionFilter?: boolean;
	hideHeader?: boolean;
	hideFeatured?: boolean;
	noPadding?: boolean;
	itemsPerRow?: number;
};

type CollectionFilterOption = {
	Id: number | string;
	Name: string;
};

const CoursesJourneyList = ({ items, onJourneyClick, hideCollectionFilter, hideHeader, hideFeatured, noPadding, limit = 999, itemsPerRow = 3 }: CoursesJourneyListProps) => {
	const { t } = useTranslation();
	const journeys = items ?? [];
	const [selectedCollectionId, setSelectedCollectionId] = useState(ALL_COLLECTIONS_ID);
	const collections = useMemo(() => getJourneyCollections(journeys), [journeys]);
	const shouldUseTabs = collections.length < 10;
	const collectionOptions = useMemo<CollectionFilterOption[]>(
		() => [
			{ Id: ALL_COLLECTIONS_ID, Name: t('COURSES.ALL_COLLECTIONS', 'All') },
			...collections,
		],
		[collections, t]
	);

	useEffect(() => {
		if (selectedCollectionId === ALL_COLLECTIONS_ID) return;
		if (collections.some((collection) => String(collection.Id) === selectedCollectionId)) return;
		setSelectedCollectionId(ALL_COLLECTIONS_ID);
	}, [collections, selectedCollectionId]);

	const filteredJourneys = useMemo(() => {
		if (selectedCollectionId === ALL_COLLECTIONS_ID) return journeys.slice(0, limit);
		return journeys.filter((journey) => String(journey.JourneyCollectionId) === selectedCollectionId).slice(0, limit);
	}, [journeys, selectedCollectionId, limit]);

	const featuredJourneys = getFeaturedJourneys(filteredJourneys);
	const allJourneys = filteredJourneys;

	const cssCLasses = `biz-courses-journey-list${noPadding ? ' no-padding' : ''}`;

	return (
		<div className={cssCLasses}>
			{!hideCollectionFilter && (
				<div className='biz-courses-journey-list__filter-bar'>
					{shouldUseTabs ? (
						<BizTabs
							tabs={collectionOptions.map((option) => ({ key: String(option.Id), title: option.Name }))}
							selectedTabKey={selectedCollectionId}
							appearance='rounded'
							onClick={(key) => setSelectedCollectionId(key)}
							ariaLabel={t('COURSES.COLLECTION_FILTER_ARIA', 'Filter by collection')}
						/>
					) : (
						<div className='biz-courses-journey-list__filter-select'>
							<BizSelect
								options={collectionOptions}
								selectedId={selectedCollectionId}
								onChange={(option) => setSelectedCollectionId(String(option?.Id ?? ALL_COLLECTIONS_ID))}
							/>
						</div>
					)}
				</div>
			)}

			{!hideFeatured && featuredJourneys.length > 0 && (
				<section className='biz-courses-journey-list__section'>
					<h2 className='biz-sub-header'>{t('COURSES.FEATURED_HEADER', 'Featured')}</h2>
					<div className='biz-courses-journey-list__featured-grid'>
						{featuredJourneys.map((journey) => (
							<JourneyFeaturedCard
								journey={journey}
								tagLabel={getTagLabel(journey)}
								onJourneyClick={onJourneyClick}
								key={journey.Id}
							/>
						))}
					</div>
				</section>
			)}

			<section className='biz-courses-journey-list__section'>
				{!hideHeader && <h2 className='biz-sub-header'>{selectedCollectionId !== 'all' ? t('COURSES.COLLECTION_SELECTED_HEADER', { collectionName: collections.find((c) => String(c.Id) === selectedCollectionId)?.Name }) : t('COURSES.ALL_LEARNING_PATHS_HEADER', 'All Learning paths')}</h2>}
				<div className='biz-courses-journey-list__all-grid' style={{ '--items-per-row': itemsPerRow } as React.CSSProperties}>
					{allJourneys.map((journey) => (
						<JourneyCard
							journey={journey}
							tagLabel={getTagLabel(journey)}
							bottomRightTagLabel={getStatusLabel(journey, t)}
							onJourneyClick={onJourneyClick}
							key={journey.Id}
						/>
					))}
				</div>
			</section>
		</div>
	);
};

const JourneyFeaturedCard = ({
	journey,
	tagLabel,
	onJourneyClick
}: {
	journey: CoursesJourney;
	tagLabel: string | undefined;
	onJourneyClick: (journey: CoursesJourney) => void;
}) => {
	const ctaPhraseKey = getCtaPhraseKey(journey.TotalProgress);
	const { t } = useTranslation();
	const hasItems = hasJourneyItems(journey);
	const responsibleUserName = getResponsibleUserName(journey);
	const progress = Math.round(journey.TotalProgress || 0);
	const progressCssClass = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started';

	return (
		<button
			type='button'
			className={`biz-courses-journey-list__featured-card ${progressCssClass}`}
			onClick={() => onJourneyClick(journey)}
			style={getJourneyBackground(journey)}
			aria-label={journey.Name}
			disabled={!hasItems}
		>
			<div className='biz-courses-journey-list__featured-content'>
				{tagLabel && <span className='biz-courses-journey-list__featured-tag'>{tagLabel}</span>}
				<h3 className='line-limit lines-2'>{journey.Name}</h3>
				{responsibleUserName && (
					<span className='biz-courses-journey-list__responsible-user'>
						{t('COURSES.RESPONSIBLE_USER', 'Responsible')}: {responsibleUserName}
					</span>
				)}
				{hasItems && 
                    <span className='biz-courses-journey-list__cta'>
                        {t(ctaPhraseKey)}
                        <i className='far fa-arrow-right' aria-hidden='true'></i>
                    </span>
                }
			</div>
		</button>
	);
};

const JourneyCard = ({
	journey,
	tagLabel,
	bottomRightTagLabel,
	onJourneyClick
}: {
	journey: CoursesJourney;
	tagLabel: string | undefined;
	bottomRightTagLabel: string | undefined;
	onJourneyClick: (journey: CoursesJourney) => void;
}) => {
	const { t } = useTranslation();
	const progress = Math.round(journey.TotalProgress || 0);
	const progressCssClass = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started';
	const moduleCount = journey.EngageObjects?.length ?? 0;
	const minutes = getJourneyMinutes(journey);
	const hasItems = hasJourneyItems(journey);
	const responsibleUserName = getResponsibleUserName(journey);

	return (
		<button
			type='button'
			className={`biz-courses-journey-list__card ${progressCssClass}`}
			onClick={() => onJourneyClick(journey)}
			aria-label={journey.Name}
			disabled={!hasItems}
		>
			<div className='biz-courses-journey-list__card-top' style={getJourneyBackground(journey)}>
				<span className={`biz-courses-journey-list__progress`}>{progress}%</span>
				{tagLabel && <span className='biz-courses-journey-list__tag'>{tagLabel}</span>}
				{bottomRightTagLabel && <span className='biz-courses-journey-list__bottom-right-tag'>{bottomRightTagLabel}</span>}
			</div>
			<div className='biz-courses-journey-list__card-bottom'>
				<h3 className='line-limit lines-2'>{journey.Name}</h3>
				<p className='line-limit lines-3'>{journey.Description}</p>
				<div className='biz-courses-journey-list__bottom-wrapper'>
					{responsibleUserName && (
						<span className='biz-courses-journey-list__responsible-user line-limit lines-1'>
							{t('COURSES.RESPONSIBLE_USER', 'Responsible')}: {responsibleUserName}
						</span>
					)}
					<div className='biz-courses-journey-list__meta'>
						<span>
							<i className='far fa-clock' /> {minutes}min
						</span>
						<span>
							<i className='far fa-book-open' /> {moduleCount} {t('COURSES.MODULES', 'modules').toLowerCase()}
						</span>
					</div>
				</div>
			</div>
		</button>
	);
};

const getFeaturedJourneys = (journeys: CoursesJourney[]) => {
	const featured = journeys.filter((journey) => journey.IsFeatured);
	return featured.slice(0, 2);
};

const hasJourneyItems = (journey: CoursesJourney) => {
	return !!journey.EngageObjects?.length;
};

const getJourneyCollections = (journeys: CoursesJourney[]) => {
	const seen = new Set<number>();
	const collections: CollectionFilterOption[] = [];

	journeys.forEach((journey) => {
		const collectionId = journey.JourneyCollectionId;
		const collectionName = journey.JourneyCollectionName?.trim();
		if (!collectionName || seen.has(collectionId)) return;
		seen.add(collectionId);
		collections.push({ Id: collectionId, Name: collectionName });
	});

	return collections;
};

const getTagLabel = (journey: CoursesJourney) => {
	const itemLabel = journey.OverrideTypeLabels?.[0]?.overrideLabels?.[0];
	if (itemLabel) return itemLabel;

	return journey.JourneyCollectionName;
};

const getStatusLabel = (journey: CoursesJourney, t: (key: string, fallback: string) => string) => {
	if (journey.TotalProgress && journey.TotalProgress > 0 && journey.TotalProgress < 100) {
		return t('COURSES.CONTINUE_BTN', 'Continue');
	}
	if (journey.TotalProgress === 100) {
		return t('COURSES.STATUS_COMPLETED', 'Completed');
	}
	if (journey.TotalProgress === 0) {
		return t('COURSES.START_BTN', 'Start');
	}
	return undefined;
};	

const getCtaPhraseKey = (progress: number) => {
	if (progress >= 100) return 'COURSES.REPEAT_BTN';
	if (progress > 0) return 'COURSES.CONTINUE_BTN';
	return 'COURSES.START_BTN';
};

const getJourneyMinutes = (journey: CoursesJourney) => {
	return journey.TotalTimeMinutes ?? (journey.EngageObjects?.length ?? 1) * DEFAULT_EO_MINUTES;
};

const getJourneyBackground = (journey: CoursesJourney) => {
	const journeyImageUrl = journey.ImageUrl;
	const fallbackObjectImageUrl = journey.EngageObjects?.find((obj) => !!obj.ImageUrl)?.ImageUrl;

	const resolvedImageUrl = getPathWithRootPath(journeyImageUrl ?? fallbackObjectImageUrl ?? '', 'mobile');
	return {
		backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : undefined,
	};
};

const getResponsibleUserName = (journey: CoursesJourney) => {
	const responsible = journey.ResponsibleUser;
	if (!responsible) return '';

	const fullName = responsible.Name;
	if (fullName) return fullName;
	return responsible.UserName ?? '';
};

export default CoursesJourneyList;
